import React from 'react';
import { usePricingFigures, usd } from '../../../hooks/usePricingFigures';
const head = 'text-[11.5px] font-bold uppercase tracking-[0.12em] text-[#c5cdd3] whitespace-nowrap';
interface Props {
  includeLeader: boolean;
  dueAtSigning: number;
}

/** Option B: what's due to start, then what repeats monthly, then hires. */
export function PricingTwoStep({
  includeLeader,
  dueAtSigning
}: Props) {
  const f = usePricingFigures(includeLeader);
  const monthlyLines = [{
    label: 'Monthly service',
    sub: f.monthlyNote,
    value: `${usd(f.monthly)} / mo`
  }, {
    label: 'Placement bonus',
    sub: f.bonusNote,
    value: `${usd(f.bonus)} / rep`,
    accent: true
  }, ...(f.retentionBonus > 0 ? [{
    label: 'Day-45 bonus',
    sub: f.retentionNote,
    value: `${usd(f.retentionBonus)} / rep`,
    accent: true
  }] : []), {
    label: 'Ad spend',
    sub: f.adsNote,
    value: `${usd(f.adSpend)} / mo`,
    muted: true
  }];
  return <div className="grid grid-cols-[1fr_1.7fr_1.15fr] gap-5 items-stretch" data-id="element-883" data-mp="m9sm9qdnq32a">
      <div className="rounded-lg border border-[#33404a] px-4 py-3.5" data-id="element-884" data-mp="r10valb2rmex">
        <div className={head} data-id="element-885" data-mp="r2byaiwp4q4z">To start</div>
        <div className="brief-display mt-2 text-[32px] font-extrabold leading-none text-white" data-id="element-886" data-mp="cceeef5sumll">
          {usd(dueAtSigning)}
        </div>
        <div className="mt-1.5 text-[12.5px] leading-snug text-[#aab4bc]" data-id="element-887" data-mp="hbze4rssmyzd">
          due at signing
        </div>
        <div className="mt-2.5 pt-2.5 border-t border-[#2a353e] text-[12.5px] leading-snug text-[#d7dde2]" data-id="element-888" data-mp="151ldbf961e7d">
          {f.showImplementation ? <>
              {usd(f.implementation)} implementation
              <span className="text-[#aab4bc]" data-id="element-889" data-mp="w2ggcdd95sxb"> + first month</span>
              {f.promoDeadlineText && <span className="block mt-1 text-[13px] font-bold text-[#6fd6b0]" data-id="element-890" data-mp="3gqclt3bykgeb">
                  Promo · {f.promoDeadlineText.toLowerCase()}
                </span>}
            </> : 'First month of service'}
        </div>
      </div>

      <div className="rounded-lg border border-[#33404a] px-4 py-3.5" data-id="element-891" data-mp="1rb1xd21ftrmk">
        <div className={head} data-id="element-892" data-mp="1mamn4jcgd08t">Every month</div>
        <ul className="mt-2 space-y-2" data-id="element-893" data-mp="1hj4kzryp0tm9">
          {monthlyLines.map(line => <li key={line.label} className="flex items-baseline justify-between gap-3" data-id="element-894" data-mp="1hh1mejjh5s5x">
              <div className="min-w-0" data-id="element-895" data-mp="1ci8gpdafj5gb">
                <span className={`text-[14.5px] font-semibold ${line.accent ? 'text-[#6fd6b0]' : line.muted ? 'text-[#c5cdd3]' : 'text-white'}`} data-id="element-896" data-mp="18j95xc20oegy">
                  {line.label}
                </span>
                <span className="ml-1.5 text-[12px] text-[#8e9aa4]" data-id="element-897" data-mp="tlgr6m4anu3t">{line.sub}</span>
              </div>
              <span className={`shrink-0 text-[15px] font-bold ${line.accent ? 'text-[#6fd6b0]' : line.muted ? 'text-[#c5cdd3]' : 'text-white'}`} data-id="element-898" data-mp="2sykxey3aqzf1">
                {line.value}
              </span>
            </li>)}
        </ul>
      </div>

      <div className="flex flex-col justify-center pl-1" data-id="element-899" data-mp="30hkakm05zupy">
        <div className={head} data-id="element-900" data-mp="30fzfmv9xu9rk">Projected hires / mo</div>
        <div className="brief-display mt-1 text-[52px] font-extrabold leading-none text-[#6fd6b0]" data-id="element-901" data-mp="359txdt5zgeo0">
          {f.hiresLow}–{f.hiresHigh}
        </div>
        <div className="mt-1 text-[12.5px] leading-snug text-[#aab4bc]" data-id="element-902" data-mp="3a9q1dseok63k">
          {usd(f.costLow)}–{usd(f.costHigh)} all-in per hire, incl. est. bonuses
        </div>
      </div>
    </div>;
}